import React, { useEffect, useRef, useState } from "react";
import Drawer from "@mui/material/Drawer";
import { alpha } from "@mui/material/styles";
import { Button, IconButton } from "@mhome/ui";
import ErrorIcon from "@mui/icons-material/Error";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import { useHistory } from "react-router-dom";
import { TextField } from "@mhome/ui";
import { useRecoilState } from "recoil";
import { userInfoState } from "../global-state";
import LoadingSpinner from "../common/loading";
import DeleteIcon from "@mui/icons-material/Delete";
import { toEntityTitle } from "../common/string-helpers";
import { getTagIcon } from "../common/tag";
import CodeIcon from "@mui/icons-material/Code";
import BugReportIcon from "@mui/icons-material/BugReport";
import {
  deleteAutomation,
  disableAutomation,
  enableAutomation,
  getAutomation,
  getAutomationDraft,
  initAutomationEditing,
  saveAutomationName,
  runningAutomation,
  copyAutomation,
  enrollAutomation,
  getAutomationCardInfo,
  shareAutomation,
  batchDeleteDraft,
  updateAutomationConfig,
  provideAutomationConfig,
  saveAutomationOverview,
  cancelAutomationEditing,
} from "../service/automation-service";
import { Typography } from "@mhome/ui";
import Markdown from "../common/markdown";
import DeleteAutomationDialog from "./delete-automation-dialog";
import { useToast } from "../common/toast";
import { useWebSocket } from "../ws/websocket";
import EditOffIcon from "@mui/icons-material/EditOff";
import EditIcon from "@mui/icons-material/Edit";
import CircularProgress from "@mui/material/CircularProgress";
import dayjs from "dayjs";
import { useCustomFormat } from "../views/after-init";
import { useEventEmitter } from "../ws/event-context";
import MSwitch from "../common/switch";
import { CircleIcon } from "lucide-react";
import { List, ListItem, ListItemIcon, ListItemText, Chip } from "@mhome/ui";
import HistoryIcon from "@mui/icons-material/History";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CanvasSelectDialog from "../dashboard/canvas-select-dialog";
import ShareIcon from "@mui/icons-material/Share";
import Tooltip from "@mui/material/Tooltip";
import ShareAutomationDialog from "./share-automation-dialog";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import SaveAsAutomationDialog from "./save-as-automation-dialog";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { createAutomationCard } from "../service/ui-service";
import DataSelector from "./automation-edit-data-selector";

const varValueCheck = (varName, varSpec) => {
  if (!varSpec || varSpec.empty) {
    return false;
  }
  return varSpec.valid;
};

const AutomationEditCardContent = ({
  params,
  onSave,
  onDelete,
  editable,
  onEnableDisable,
}) => {
  const [edit, setEdit] = useState(false);
  const [editOnly, setEditOnly] = useState(false);
  const [titleEditMode, setTitleEditMode] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedVar, setSelectedVar] = useState("");
  const [loading, setLoading] = useState(true);
  const [varSaving, setVarSaving] = useState(false);
  const [userInfo] = useRecoilState(userInfoState);
  const history = useHistory();
  const { sendAndWaitWs } = useWebSocket();
  const [spinButton, setSpinButton] = useState(null);
  const customFormat = useCustomFormat();

  const [sessionId, setSessionId] = useState(null);
  const [temp, setTemp] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [running, setRunning] = useState(false);
  const [runnable, setRunnable] = useState(false);
  const [requiredInstalls, setRequiredInstalls] = useState([]);
  const [enablingDisabling, setEnablingDisabling] = useState(false);
  const [enableCorrupted, setEnableCorrupted] = useState(false);
  const [automationName, setAutomationName] = useState(null);
  const [automationDescription, setAutomationDescription] = useState(null);
  const [vars, setVars] = useState({});
  const [varDescriptions, setVarDescriptions] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const { successToast, errorToast } = useToast();
  const [type, setType] = useState(null);
  const [timestamp, setTimestamp] = useState(0);
  const [createdAt, setCreatedAt] = useState(0);
  const [nameError, setNameError] = useState("");
  const [currentTime, setCurrentTime] = useState(dayjs());
  const { subscribe } = useEventEmitter();
  const [oldDescr, setOldDescr] = useState("");
  const lastStatusTimestampRef = useRef(0);
  const [chips, setChips] = useState([]);
  const [expireAt, setExpireAt] = useState(0);

  const getComponentBgColor = useComponentBackgroundColor();

  // canvas related
  const [canvasDialogOpen, setCanvasDialogOpen] = useState(false);

  const [currentCanvases, setCurrentCanvases] = useState([]);
  const [canvases, setCanvases] = useState([]);
  const [widgetizeVars, setWidgetizeVars] = useState([]);
  const [localWidgets, setLocalWidgets] = useState([]);
  // share related
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubDraft, setIsSubDraft] = useState(false);
  const [draftCreatedAt, setDraftCreatedAt] = useState(0);
  const [isCorrupted, setIsCorrupted] = useState(false);

  // Use ref to store current sessionId for cleanup
  const sessionIdRef = useRef(null);

  // Update refs when values change
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    return () => {
      handleAutomationCancel();
    };
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000); // Update every second

    return () => clearInterval(intervalId); // Cleanup interval on unmount
  }, []);

  useEffect(() => {
    console.debug("params", params);
    if (params && sendAndWaitWs) {
      if (params.inspireId) {
        setEditOnly(true);
        setIsSubDraft(false);
        setTitleEditMode(true);
        initEdit(
          null,
          params.inspireId,
          params.inspireVersion,
          null,
          params.args
        );
      } else if (params.scriptId) {
        if (params.forceEdit) {
          setEditOnly(true);
          setIsSubDraft(false);
          initEdit(params.automationId, null, null, params.scriptId, null);
        } else {
          setIsSubDraft(true);
          initReadDraft(params.scriptId);
        }
      } else {
        setIsSubDraft(false);
        initRead(params.automationId);
      }
    }
    return () => {};
  }, [params]);

  useEffect(() => {
    if (automationName === "") {
      setNameError("Name cannot be empty.");
    } else if (
      automationName &&
      automationName.trim().toLowerCase() === "this"
    ) {
      setNameError("Name cannot be 'this'.");
      setTitleEditMode(true);
    } else {
      setNameError("");
    }
  }, [automationName]);

  useEffect(() => {
    const { unsubscribe } = subscribe((target, data) => {
      if (target === "/automation/history/reportUpdate") {
        const payload = JSON.parse(data.payload);
        if (payload && payload.automationId === params.automationId) {
          setTimestamp(payload.timestamp);
        }
      } else if (target === "/automation/runningStatus/reportUpdate") {
        const payload = JSON.parse(data.payload);
        if (
          payload &&
          payload.automationId === params.automationId &&
          payload.timestamp > lastStatusTimestampRef.current
        ) {
          lastStatusTimestampRef.current = payload.timestamp;
          setRunning(payload.running);
        }
      }
    });
    return () => {
      unsubscribe();
    };
  }, [params]);

  const enterEdit = async () => {
    if (edit) {
      return;
    }
    setEdit(true);
    initEdit(params.automationId, null, null, params.scriptId, null);
  };

  const initRead = async (automationId) => {
    setLoading(true);
    setError(false);
    setErrorMessage("");
    setIsCorrupted(false);
    const resp = await getAutomation(sendAndWaitWs, automationId, false);
    if (resp) {
      if (resp.error) {
        setError(true);
        if (resp.error === "NOT_FOUND") {
          setErrorMessage("Automation not found or deleted");
        } else {
          setErrorMessage("Error loading automation");
        }
        setLoading(false);
        return;
      }
      setAutomationName(resp["name"]);
      setAutomationDescription(resp["description"]);
      setOldDescr(resp["description"]);
      setRunnable(resp["runnable"]);
      setEnabled(resp["enabled"]);
      setEnableCorrupted(resp["corrupted"] || false);
      setTemp(resp["temp"]);
      setRunning(resp["running"]);
      setType(resp["type"]);
      setTimestamp(resp["lastRunAt"] || 0);
      setCreatedAt(resp["createdAt"] || 0);
      setExpireAt(resp["expireAt"] || 0);
      setLoading(false);
      setEdit(false);
      if (resp.widgetizeInfo) {
        const wvKeys = Object.keys(resp.widgetizeInfo.widgetized ?? {});
        const lvKeys = Object.keys(resp.widgetizeInfo.localWidgets ?? {});
        setWidgetizeVars([...wvKeys, ...lvKeys]);
        setLocalWidgets(lvKeys);
      }
    } else {
      setError(true);
      setErrorMessage("Error loading automation");
      setLoading(false);
    }
  };

  const initReadDraft = async (scriptId) => {
    setLoading(true);
    setError(false);
    setErrorMessage("");
    setIsCorrupted(false);
    const resp = await getAutomationDraft(sendAndWaitWs, scriptId);
    if (resp) {
      if (resp.error) {
        setError(true);
        setErrorMessage("Error loading automation draft");
        setLoading(false);
        setIsCorrupted(true);
        return;
      }
      if (resp.corrupted) {
        setIsCorrupted(true);
      }
      setAutomationDescription(resp.description);
      setDraftCreatedAt(resp.createdAt || 0);
      setLoading(false);
      setEdit(false);
    } else {
      setError(true);
      setErrorMessage("Error loading automation draft");
      setLoading(false);
    }
  };

  const loadCanvasInfo = async () => {
    const resp = await getAutomationCardInfo(
      sendAndWaitWs,
      params.automationId
    );
    if (resp) {
      console.debug(resp);
      setCanvases(resp.allCanvas);
      setCurrentCanvases(resp.currentCanvas.map((c) => c.id));
    } else {
      errorToast("Error loading data!");
    }
  };

  const handleWidgetize = (varName, include) => {
    if (include) {
      setWidgetizeVars((prev) =>
        prev.includes(varName) ? prev : [...prev, varName]
      );
    } else {
      setWidgetizeVars((prev) => prev.filter((v) => v !== varName));
    }
  };

  const handleLocalWidgetize = (varName, include) => {
    if (include) {
      setLocalWidgets((prev) =>
        prev.includes(varName) ? prev : [...prev, varName]
      );
    } else {
      setLocalWidgets((prev) => prev.filter((v) => v !== varName));
    }
  };

  const initEdit = async (
    automationId,
    inspireId,
    inspireVersion,
    scriptId,
    args
  ) => {
    setLoading(true);
    setIsCorrupted(false);
    const resp = await initAutomationEditing(
      sendAndWaitWs,
      automationId,
      inspireId,
      inspireVersion,
      scriptId,
      args
    );
    if (resp) {
      if (resp.automationOverview) {
        console.debug("type", resp.automationOverview);
        const automationOverview = resp.automationOverview;
        setSessionId(resp["sessionId"]);
        setAutomationName(automationOverview["name"]);
        setOldDescr(automationOverview["description"]);
        setAutomationDescription(automationOverview["description"]);
        setVars(automationOverview["vars"]);
        setVarDescriptions(automationOverview["varDescriptions"]);
        setRunnable(automationOverview["runnable"]);
        setEnabled(automationOverview["enabled"]);
        setEnableCorrupted(false);
        setExpireAt(automationOverview["expireAt"] || 0);
        setLoading(false);
        setEdit(true);
        setChips([]);
      } else if (resp.requiredInstalls) {
        setRequiredInstalls(resp.requiredInstalls);
        setLoading(false);
      }
    } else {
      errorToast("Error loading automation.");
    }
  };

  const handleVarValueSave = async (varData) => {
    try {
      setVarSaving(true);
      const jsonData = await updateAutomationConfig(
        sendAndWaitWs,
        sessionId,
        varData
      );
      if (jsonData.error) {
        throw new Error(jsonData.error);
      }
      setAutomationDescription(jsonData["description"]);
      setVars(jsonData["vars"]);
      setShowDrawer(false);
      setTimeout(() => {
        setVarSaving(false);
      }, 200);
    } catch (error) {
      console.error("Error fetching data:", error);
      setVarSaving(false);
    }
  };

  const handleVarValueFetch = async (varData) => {
    try {
      const jsonData = await provideAutomationConfig(
        sendAndWaitWs,
        sessionId,
        varData
      );
      if (jsonData.error) {
        throw new Error(jsonData.error);
      }
      console.debug("handleVarValueFetch", jsonData);
      return jsonData;
    } catch (error) {
      errorToast("Error!");
    }
  };

  const handleDrawerClose = () => {
    setShowDrawer(false);
  };

  const handleAutomationNameSave = async () => {
    const response = await saveAutomationName(
      sendAndWaitWs,
      params.automationId,
      automationName
    );
    if (response.error) {
      errorToast("Error!");
    } else {
      successToast("Name Saved");
    }
  };

  const handleDescriptionCancel = () => {
    console.debug("handleDescriptionCancel");
    handleAutomationCancel(); // Reuse the cancel function
    setAutomationDescription(oldDescr);
    setEdit(!edit);
    console.debug("handleDescriptionCancel done", type);
  };

  const isWidgetable = (v) => {
    const primitive = vars[v].type.indexOf(".") === -1;
    const widget = vars[selectedVar].type === "basic.widget.Widget";
    return primitive || widget;
  };

  const handleAutomationSave = async () => {
    try {
      setLoading(true);
      let saveParams = null;
      if (params.scriptId) {
        // draft
        saveParams = {
          preferredName: automationName,
          automationId: params.automationId,
          scriptId: params.scriptId,
          debug: userInfo.setting?.debugMode || false,
          widgetizeVars,
        };
      } else if (params.automationId) {
        saveParams = {
          preferredName: automationName,
          automationId: params.automationId,
          debug: userInfo.setting?.debugMode || false,
          widgetizeVars,
        };
      } else if (params.inspireId) {
        saveParams = {
          inspireId: params.inspireId,
          inspireVersion: params.inspireVersion,
          preferredName: automationName,
          debug: userInfo.setting?.debugMode || false,
          widgetizeVars,
        };
      } else {
        return null;
      }

      const jsonData = await saveAutomationOverview(
        sendAndWaitWs,
        sessionId,
        saveParams
      );
      if (jsonData.error) {
        throw new Error(jsonData.error);
      }
      setRunnable(jsonData["runnable"]);
      setEnabled(jsonData["enabled"]);
      setEnableCorrupted(jsonData["corrupted"] || false);
      successToast("Saved");
      setLoading(false);
      setEdit(false);
      onSave(jsonData.automationId);
    } catch (error) {
      errorToast("Save error!");
      console.error("Error saving data:", error);
      setLoading(false);
    }
  };

  const handleAutomationCancel = () => {
    if (sessionIdRef.current) {
      cancelAutomationEditing(sendAndWaitWs, sessionIdRef.current);
    }
  };

  const handleEnableDisable = async (event, enableDisable) => {
    setEnablingDisabling(true);
    if (enableDisable) {
      const response = await enableAutomation(
        sendAndWaitWs,
        params.automationId
      );
      if (response && !response.error) {
        successToast("Enabled");
        setEnabled(response.enabled);
        setEnableCorrupted(response.corrupted || false);
        if (onEnableDisable) {
          onEnableDisable(true);
        }
      } else {
        errorToast("Error to enable the automation.");
      }
    } else {
      const response = await disableAutomation(
        sendAndWaitWs,
        params.automationId
      );
      if (response && !response.error) {
        successToast("Disabled");
        setEnabled(response.enabled);
        setEnableCorrupted(false);
        if (onEnableDisable) {
          onEnableDisable(false);
        }
      } else {
        errorToast("Error to disable the automation.");
      }
    }
    setEnablingDisabling(false);
  };

  const handleStartTaskFlow = async (running) => {
    if (running) {
      const success = await runningAutomation(
        sendAndWaitWs,
        params.automationId
      );
      if (success) {
        successToast("Started");
      } else {
        errorToast("Error!");
      }
    }
  };

  const handleCopy = async () => {
    setSpinButton("copy");
    const resp = await copyAutomation(
      sendAndWaitWs,
      params.automationId,
      `Copy - ${automationName}`
    );
    if (resp) {
      onSave(resp.automationId);
    } else {
      errorToast("Error!");
    }
    setSpinButton(null);
  };

  const handleAddToDashboard = async () => {
    await loadCanvasInfo();
    setCanvasDialogOpen(true);
  };

  const handleCanvasSelect = async (canvasId) => {
    setCanvasDialogOpen(false);
    setSpinButton("dashboard");
    const success = await createAutomationCard(
      sendAndWaitWs,
      canvasId,
      params.automationId
    );
    if (success) {
      successToast("Added to dashboard");
    } else {
      errorToast("Failed to add to dashboard");
    }
    setSpinButton(null);
  };

  const handleDelete = async () => {
    let success = false;

    if (isSubDraft && params.scriptId) {
      // Delete draft using batchDeleteDraft
      success = await batchDeleteDraft(sendAndWaitWs, [params.scriptId]);
    } else {
      // Delete automation using deleteAutomation
      success = await deleteAutomation(
        sendAndWaitWs,
        params.automationId,
        true
      );
    }

    if (success) {
      onDelete(!isSubDraft, params.scriptId, type);
    } else {
      errorToast("Error. Please retry.");
    }
    setDeleteConfirm(false);
  };

  const generateColor = (name) => {
    // Predefined colors spread across hue 60-300
    const colors = [
      "hsl(60, 30%, 50%)", // Yellow
      "hsl(90, 30%, 50%)", // Light Green
      "hsl(150, 30%, 50%)", // Cyan
      "hsl(180, 30%, 50%)", // Blue
      "hsl(210, 30%, 50%)", // Indigo
      "hsl(240, 30%, 50%)", // Deep Purple
      "hsl(300, 30%, 50%)", // Rose
      "hsl(330, 30%, 50%)", // Rose
    ];

    // Create a simple hash from the name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = (hash << 5) - hash + name.charCodeAt(i);
      hash = hash & hash; // Convert to 32-bit integer
    }

    // Use the hash to select a color from our predefined array
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const handleMarkdownLink = ({ children, href }) => {
    const handleClick = (event) => {
      event.preventDefault();
      const varPart = href.substring(href.lastIndexOf("#") + 1);
      const parts = varPart.split("-");
      let varName = "$" + parts[0];
      if (parts[0].includes("+")) {
        const [name, flag] = parts[0].split("+");
        varName = "$" + name;
      }
      setSelectedVar(varName);
      setShowDrawer(true);
    };

    const varPart = href.substring(href.lastIndexOf("#") + 1);
    const parts = varPart.split("-");
    let varName = "$" + parts[0];

    // Parse tag and flag
    let varTag = "";
    let varFlag = 0;

    // Check if name part contains a flag
    if (parts[0].includes("+")) {
      const [name, flag] = parts[0].split("+");
      varName = "$" + name;
      varFlag = Number(flag);
    } else if (parts.length > 1) {
      // Check if last part contains a flag
      const lastPart = parts[parts.length - 1];
      if (lastPart.includes("+")) {
        const [tag, flag] = lastPart.split("+");
        varTag = tag;
        varFlag = Number(flag);
      } else {
        varTag = lastPart;
      }
    }

    let color;
    if ((varFlag & 1) > 0) {
      // Use color based on variable name
      color = generateColor(varName);
    } else {
      // Use default color logic
      color = "hsl(var(--foreground))";
    }

    const localW = localWidgets.indexOf(varName) > -1;
    const valid = varValueCheck(varName, vars[varName]) || localW;
    const text = localW
      ? `Auto Widget (${varDescriptions[varName] || ""})`
      : toEntityTitle(children, (varFlag & 2) > 0);

    return (
      <Chip
        label={getDisplayContent(text, varTag)}
        onClick={handleClick}
        size="small"
        variant={valid ? "outlined" : "outlined"}
        color={
          valid
            ? widgetizeVars.indexOf(varName) > -1
              ? "secondary"
              : "primary"
            : "destructive"
        }
        style={{
          height: "24px",
          borderRadius: "12px",
          cursor: "pointer",
          backgroundColor: valid
            ? widgetizeVars.indexOf(varName) > -1
              ? "hsl(var(--secondary))"
              : "hsl(var(--primary) / 0.1)"
            : "hsl(var(--destructive) / 0.3)",
          color: valid
            ? widgetizeVars.indexOf(varName) > -1
              ? "hsl(var(--foreground))"
              : color
            : color,
          border: valid
            ? `1px solid hsl(var(--primary) / 0.2)`
            : `1px solid hsl(var(--destructive))`,
          textDecoration: valid ? "none" : "underline",
          textDecorationStyle: valid ? "none" : "wavy",
          fontWeight: "bold",
          padding: "0 8px",
          fontSize: "0.875rem",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = valid
            ? widgetizeVars.indexOf(varName) > -1
              ? "hsl(var(--secondary))"
              : "hsl(var(--primary) / 0.2)"
            : "hsl(var(--destructive) / 0.2)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = valid
            ? widgetizeVars.indexOf(varName) > -1
              ? "hsl(var(--secondary))"
              : "hsl(var(--primary) / 0.1)"
            : "hsl(var(--destructive) / 0.3)";
        }}
      />
    );
  };

  const getDisplayContent = (children, varTag) => {
    const icon = getTagIcon(varTag);
    const displayContent =
      varTag && icon ? (
        <>
          <span
            style={{
              marginTop: "1px",
              marginLeft: "2px",
              marginRight: "2px",
              height: "20px",
              display: "inline-block",
              lineHeight: "20px",
              verticalAlign: "top",
            }}
          >
            {icon}
          </span>
          <span style={{ marginRight: "2px", fontSize: "18px" }}>
            {children}
          </span>
        </>
      ) : (
        <span
          style={{ marginRight: "2px", marginLeft: "2px", fontSize: "18px" }}
        >
          {children}
        </span>
      );

    return displayContent;
  };

  const handleMarkdownNonEditLink = ({ children, href }) => {
    const handleClick = (event) => {
      event.preventDefault();
    };
    const varPart = href.substring(href.lastIndexOf("#") + 1);
    const parts = varPart.split("-");
    let varName = "$" + parts[0];

    // Parse tag and flag
    let varTag = "";
    let varFlag = 0;

    // Check if name part contains a flag
    if (parts[0].includes("+")) {
      const [name, flag] = parts[0].split("+");
      varName = "$" + name;
      varFlag = Number(flag);
    } else if (parts.length > 1) {
      // Check if last part contains a flag
      const lastPart = parts[parts.length - 1];
      if (lastPart.includes("+")) {
        const [tag, flag] = lastPart.split("+");
        varTag = tag;
        varFlag = Number(flag);
      } else {
        varTag = lastPart;
      }
    }

    let color;
    if ((varFlag & 1) > 0) {
      // Use color based on variable name
      color = generateColor(varName);
    } else {
      // Use default color logic
      color = "hsl(var(--foreground))";
    }

    let text = toEntityTitle(children, (varFlag & 2) > 0);
    if (localWidgets.indexOf(varName) > -1) {
      text = `Auto Widget (${varDescriptions[varName] || ""})`;
    }

    return (
      <Chip
        label={getDisplayContent(text, varTag)}
        onClick={handleClick}
        size="small"
        style={{
          height: "24px",
          borderRadius: "12px",
          cursor: "default",
          backgroundColor:
            widgetizeVars.indexOf(varName) > -1
              ? "hsl(var(--secondary))"
              : "hsl(var(--primary) / 0.1)",
          color:
            widgetizeVars.indexOf(varName) > -1
              ? "hsl(var(--foreground))"
              : color,
          border: `1px solid hsl(var(--primary) / 0.2)`,
          fontWeight: "bold",
          padding: "0 8px",
          fontSize: "0.875rem",
        }}
      />
    );
  };

  const handleMarkdownInput = ({
    children,
    desc,
    var: varProp,
    tag,
    flag,
    value,
  }) => {
    const handleClick = (event) => {
      event.preventDefault();
      const varName = "$" + varProp;
      setSelectedVar(varName);
      setShowDrawer(true);
    };

    const varName = "$" + varProp;
    const varTag = tag || "";
    const varFlag = Number(flag) || 0;

    let color;
    if ((varFlag & 1) > 0) {
      // Use color based on variable name
      color = generateColor(varName);
    } else {
      // Use default color logic
      color = "hsl(var(--foreground))";
    }

    const localW = localWidgets.indexOf(varName) > -1;
    const valid = varValueCheck(varName, vars[varName]) || localW;

    // Use value attribute if present, otherwise fallback to desc
    const displayText = value || desc || "";
    const text = localW
      ? `Auto Widget (${varDescriptions[varName] || ""})`
      : toEntityTitle(displayText, (varFlag & 2) > 0);

    return (
      <Chip
        label={getDisplayContent(text, varTag)}
        onClick={handleClick}
        size="small"
        style={{
          height: "24px",
          borderRadius: "12px",
          cursor: "pointer",
          backgroundColor: valid
            ? widgetizeVars.indexOf(varName) > -1
              ? "hsl(var(--secondary))"
              : "hsl(var(--accent))"
            : "hsl(var(--destructive) / 0.3)",
          color: valid
            ? widgetizeVars.indexOf(varName) > -1
              ? "hsl(var(--foreground))"
              : color
            : color,
          border: valid
            ? `1px solid hsl(var(--primary) / 0.2)`
            : `1px solid hsl(var(--destructive))`,
          textDecoration: "none",
          fontWeight: "bold",
          padding: "0 8px",
          fontSize: "0.875rem",
        }}
      />
    );
  };

  const handleMarkdownNonEditInput = ({
    children,
    desc,
    var: varProp,
    tag,
    flag,
    value,
  }) => {
    const handleClick = (event) => {
      event.preventDefault();
    };

    const varName = "$" + varProp;
    const varTag = tag || "";
    const varFlag = Number(flag) || 0;

    let color;
    if ((varFlag & 1) > 0) {
      // Use color based on variable name
      color = generateColor(varName);
    } else {
      // Use default color logic
      color = "hsl(var(--foreground))";
    }

    // Use value attribute if present, otherwise fallback to desc
    const displayText = value || desc || "";
    let text = toEntityTitle(displayText, (varFlag & 2) > 0);
    if (localWidgets.indexOf(varName) > -1) {
      text = `Auto Widget (${varDescriptions[varName] || ""})`;
    }

    return (
      <Chip
        label={getDisplayContent(text, varTag)}
        onClick={handleClick}
        size="small"
        style={{
          height: "24px",
          borderRadius: "12px",
          cursor: "default",
          backgroundColor:
            widgetizeVars.indexOf(varName) > -1
              ? "hsl(var(--secondary))"
              : value
              ? "hsl(var(--accent))"
              : "hsl(var(--warning) / 0.1)",
          color:
            widgetizeVars.indexOf(varName) > -1
              ? "hsl(var(--foreground))"
              : color,
          border: value
            ? `1px solid hsl(var(--primary) / 0.2)`
            : `1px solid hsl(var(--warning) / 0.6)`,
          fontWeight: "bold",
          padding: "0 8px",
          fontSize: "0.875rem",
        }}
      />
    );
  };

  const handleJumpClick = (buttonType, path) => {
    console.debug(path);
    setSpinButton(buttonType);
    history.push(path);
    setSpinButton(null);
  };

  const handleEnrollTemp = async () => {
    const resp = await enrollAutomation(
      sendAndWaitWs,
      params.automationId,
      automationName
    );
    if (resp) {
      return resp.automationId;
    } else {
      throw new Error("Save failed");
    }
  };

  const handleSaveDialogOpen = () => {
    setSaveDialogOpen(true);
  };

  const handleSaveDialogClose = () => {
    setSaveDialogOpen(false);
  };

  const handleOpenSavedAutomation = (automationId) => {
    onSave(automationId);
  };

  const handleTitleEditToggle = () => {
    if (nameError === "") {
      setTitleEditMode(!titleEditMode);
    }
    if (titleEditMode) {
      if (params.automationId) {
        // can update name only if the automation already exists
        handleAutomationNameSave();
      }
    }
    setTitleEditMode(!titleEditMode);
  };

  const handleShare = async () => {
    setSpinButton("share");
    try {
      const resp = await shareAutomation(sendAndWaitWs, params.automationId);
      if (resp && resp.sharedId) {
        const url = `${window.location.origin}/automation/shared/${resp.sharedId}`;
        setShareLink(url);
        setShareDialogOpen(true);
      } else {
        errorToast("Share failed");
      }
    } catch (e) {
      errorToast("Share failed");
    }
    setSpinButton(null);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
  };

  return (
    <>
      {loading ? (
        <div>
          <LoadingSpinner size={40} padding={3} />
        </div>
      ) : error ? (
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "16px",
            paddingTop: "32px",
            paddingBottom: "32px",
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
            minHeight: "200px",
          }}
        >
          <ErrorIcon
            sx={{
              fontSize: 48,
              color: "hsl(var(--destructive))",
              marginBottom: 2,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: "hsl(var(--foreground))",
              textAlign: "center",
              fontWeight: 500,
            }}
          >
            {errorMessage}
          </Typography>
        </div>
      ) : (
        <>
          {requiredInstalls && requiredInstalls.length > 0 ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                paddingTop: "16px",
                paddingLeft: "16px",
                paddingRight: "16px",
                paddingBottom: "16px",
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
                borderRadius: "16px",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 2,
                  color: "text.primary",
                  fontWeight: 500,
                }}
              >
                Following required integrations are not installed:
              </Typography>

              <List style={{ width: "100%", padding: 0 }}>
                {requiredInstalls.map((integration, index) => (
                  <ListItem
                    key={integration.integrationId}
                    style={{
                      paddingLeft: 0,
                      paddingRight: 0,
                      paddingTop: "8px",
                      paddingBottom: "8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      borderBottom:
                        index < requiredInstalls.length - 1
                          ? `1px solid hsl(var(--border))`
                          : "none",
                    }}
                  >
                    <ListItemIcon style={{ minWidth: "auto" }}>
                      <CircleIcon
                        style={{
                          fontSize: 8,
                          color: "hsl(var(--muted-foreground))",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <span
                          style={{
                            color: "hsl(var(--muted-foreground))",
                            fontSize: "0.95rem",
                          }}
                        >
                          {integration.integrationName}
                        </span>
                      }
                      style={{
                        margin: 0,
                      }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      href={`/integration?id=${integration.integrationId}`}
                      size="small"
                      sx={{
                        minWidth: "90px",
                        textTransform: "none",
                        boxShadow: "none",
                        ml: "auto",
                        "&:hover": {
                          boxShadow: "none",
                        },
                      }}
                    >
                      View
                    </Button>
                  </ListItem>
                ))}
              </List>
            </div>
          ) : (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                paddingTop: "8px",
                paddingLeft: "8px",
                paddingRight: "8px",
                paddingBottom: "8px",
                backgroundColor: "hsl(var(--card))",
                borderRadius: "16px",
                width: "100%",
                maxWidth: "100%",
                overflowX: "auto",
              }}
            >
              {isSubDraft ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: "8px",
                    marginBottom: "8px",
                    paddingLeft: "8px",
                    paddingRight: "8px",
                  }}
                ></div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: "8px",
                    marginBottom: "8px",
                    paddingLeft: "8px",
                    paddingRight: "8px",
                  }}
                >
                  {params.automationId && !temp && (
                    <IconButton
                      onClick={handleTitleEditToggle}
                      sx={{ color: "text.primary", mr: 1 }}
                      disabled={
                        nameError !== "" ||
                        !automationName ||
                        automationName.trim() === ""
                      }
                    >
                      {titleEditMode ? <EditOffIcon /> : <EditIcon />}
                    </IconButton>
                  )}
                  {params.inspireId && params.inspireId.startsWith("P") && (
                    <AutoAwesomeIcon
                      sx={{
                        fontSize: 28,
                        color: "hsl(var(--primary))",
                        mr: 1,
                      }}
                    />
                  )}
                  {params.inspireId && params.inspireId.startsWith("S") && (
                    <ShareIcon
                      sx={{
                        fontSize: 28,
                        color: "hsl(var(--primary))",
                        mr: 1,
                      }}
                    />
                  )}
                  <div style={{ flexGrow: 1 }}>
                    {titleEditMode ? (
                      <TextField
                        value={automationName}
                        onChange={(e) => setAutomationName(e.target.value)}
                        fullWidth
                        variant="outlined"
                        size="large"
                        sx={{ marginRight: 1 }}
                        error={nameError !== ""}
                        helperText={nameError}
                      />
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Typography variant="h5">{automationName}</Typography>
                      </div>
                    )}
                    {temp && (
                      <Typography variant="subtitle2" color="text.secondary">
                        Temporary task from request history, expires at{" "}
                        {dayjs(expireAt).format("MMM D, YYYY h:mm A")}.
                      </Typography>
                    )}
                  </div>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: "8px",
                  paddingRight: "8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    width: "100%",
                  }}
                >
                  {edit && (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          flexWrap: "wrap",
                          justifyContent: "flex-start",
                        }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleAutomationSave}
                          disabled={!edit || spinButton !== null}
                          sx={{
                            textTransform: "none",
                            boxShadow: "none",
                            minWidth: "64px",
                            height: "36px",
                            "&:hover": {
                              boxShadow: "none",
                            },
                          }}
                        >
                          Save
                        </Button>
                        {!editOnly && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleDescriptionCancel}
                            sx={{
                              textTransform: "none",
                              boxShadow: "none",
                              minWidth: "64px",
                              height: "36px",
                              "&:hover": {
                                boxShadow: "none",
                              },
                            }}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {!edit && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        flexWrap: "wrap",
                        justifyContent: "flex-start",
                      }}
                      className="automation-overview-icons"
                    >
                      {!isSubDraft && runnable && params.automationId ? (
                        type === "RECURRING" ? (
                          enablingDisabling ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "40px",
                                height: "40px",
                              }}
                            >
                              <CircularProgress size={24} />
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "40px",
                                height: "40px",
                              }}
                            >
                              <MSwitch
                                checked={enabled}
                                disabled={
                                  !runnable || edit || spinButton !== null
                                }
                                onChange={handleEnableDisable}
                                inputProps={{ "aria-label": "controlled" }}
                              />
                            </div>
                          )
                        ) : running ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "40px",
                              height: "40px",
                            }}
                          >
                            <CircularProgress size={24} />
                          </div>
                        ) : (
                          <IconButton
                            disabled={!runnable || edit}
                            onClick={(e) => {
                              handleStartTaskFlow(true);
                              e.stopPropagation();
                            }}
                            sx={{
                              "&.Mui-disabled": {
                                opacity: 0.5,
                              },
                              width: "40px",
                              height: "40px",
                            }}
                          >
                            <PlayCircleFilledWhiteIcon
                              color={runnable ? "primary" : "disabled"}
                            />
                          </IconButton>
                        )
                      ) : !isSubDraft && !params.inspireId ? (
                        <IconButton
                          disabled
                          sx={{ color: "red", width: "40px", height: "40px" }}
                        >
                          <ErrorIcon />
                        </IconButton>
                      ) : (
                        <></>
                      )}

                      {!isSubDraft &&
                        timestamp !== 0 &&
                        params.automationId && (
                          <IconButton
                            aria-label="automation history"
                            onClick={() =>
                              handleJumpClick(
                                "history",
                                "/automation/history?id=" + params.automationId
                              )
                            }
                            disabled={spinButton !== null}
                            sx={{
                              color: "hsl(var(--primary))",
                              width: "40px",
                              height: "40px",
                            }}
                          >
                            <HistoryIcon />
                          </IconButton>
                        )}

                      {params.automationId && (
                        <>
                          {!isSubDraft && temp && (
                            <IconButton
                              aria-label="save as automation"
                              onClick={handleSaveDialogOpen}
                              disabled={spinButton !== null}
                              sx={{
                                color: "hsl(var(--primary))",
                                width: "40px",
                                height: "40px",
                              }}
                            >
                              <SaveAltIcon />
                            </IconButton>
                          )}
                          {!isSubDraft && !temp && (
                            <IconButton
                              aria-label="automation history"
                              onClick={() => {
                                history.push(
                                  `/automation/history/statistics?id=${params.automationId}`
                                );
                              }}
                              disabled={spinButton !== null}
                              sx={{
                                color: "hsl(var(--primary))",
                                width: "40px",
                                height: "40px",
                              }}
                            >
                              <AssessmentIcon />
                            </IconButton>
                          )}
                          <IconButton
                            aria-label="delete"
                            disabled={spinButton !== null}
                            onClick={() => setDeleteConfirm(true)}
                            sx={{
                              color: "hsl(var(--destructive))",
                              width: "40px",
                              height: "40px",
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                          {!isSubDraft && !temp && (
                            <Tooltip title="Add to Dashboard">
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "40px",
                                  height: "40px",
                                }}
                              >
                                <IconButton
                                  aria-label="add to dashboard"
                                  disabled={spinButton !== null}
                                  onClick={handleAddToDashboard}
                                  sx={{ color: "hsl(var(--primary))" }}
                                >
                                  {spinButton === "dashboard" ? (
                                    <CircularProgress size={24} />
                                  ) : (
                                    <DashboardIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {!isSubDraft &&
                            !temp &&
                            (spinButton === "copy" ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "40px",
                                  height: "40px",
                                }}
                              >
                                <CircularProgress size={24} />
                              </div>
                            ) : (
                              <IconButton
                                aria-label="copy"
                                disabled={spinButton !== null}
                                onClick={handleCopy}
                                sx={{
                                  color: "hsl(var(--primary))",
                                  width: "40px",
                                  height: "40px",
                                }}
                              >
                                <FileCopyIcon />
                              </IconButton>
                            ))}
                          {!isSubDraft && !temp && (
                            <Tooltip title="Share">
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "40px",
                                  height: "40px",
                                }}
                              >
                                <IconButton
                                  aria-label="share"
                                  disabled={spinButton !== null}
                                  onClick={handleShare}
                                  sx={{ color: "hsl(var(--primary))" }}
                                >
                                  {spinButton === "share" ? (
                                    <CircularProgress size={24} />
                                  ) : (
                                    <ShareIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {userInfo.setting?.scriptMode &&
                            (spinButton === "script" ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "40px",
                                  height: "40px",
                                }}
                              >
                                <CircularProgress size={24} />
                              </div>
                            ) : (
                              <IconButton
                                onClick={() =>
                                  handleJumpClick(
                                    "script",
                                    isSubDraft
                                      ? "/automation/script?draft=" +
                                          params.scriptId
                                      : "/automation/script?id=" +
                                          params.automationId
                                  )
                                }
                                disabled={spinButton !== null}
                                sx={{
                                  color: "hsl(var(--primary))",
                                  width: "40px",
                                  height: "40px",
                                }}
                              >
                                <CodeIcon />
                              </IconButton>
                            ))}
                          {!isSubDraft &&
                            userInfo.setting?.debugMode &&
                            (spinButton === "debug" ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "40px",
                                  height: "40px",
                                }}
                              >
                                <CircularProgress size={24} />
                              </div>
                            ) : (
                              <IconButton
                                onClick={() =>
                                  handleJumpClick(
                                    "debug",
                                    "/automation/debug?id=" +
                                      params.automationId
                                  )
                                }
                                disabled={spinButton !== null}
                                sx={{
                                  color: "hsl(var(--primary))",
                                  width: "40px",
                                  height: "40px",
                                }}
                              >
                                <BugReportIcon />
                              </IconButton>
                            ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginTop: "4px",
                  paddingLeft: "8px",
                  paddingRight: "8px",
                }}
              >
                {chips.map((chip, index) => (
                  <Chip
                    key={index}
                    label={chip.name}
                    size="small"
                    color={chip.color === "dashboard" ? "secondary" : "default"}
                    style={{
                      marginBottom: "8px",
                      backgroundColor:
                        chip.color === "dashboard"
                          ? "hsl(var(--secondary))"
                          : "hsl(var(--background))",
                      color: "hsl(var(--foreground))",
                      height: "24px",
                    }}
                  />
                ))}
              </div>
              {!edit && (
                <div
                  style={{
                    marginTop: 0,
                    paddingLeft: "8px",
                    paddingRight: "8px",
                  }}
                >
                  {isSubDraft ? (
                    draftCreatedAt !== 0 ? (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        <Chip
                          label={`Created: ${customFormat(
                            dayjs(draftCreatedAt),
                            currentTime
                          )}`}
                          variant="outlined"
                          size="small"
                          style={{
                            backgroundColor: "transparent",
                            color: "hsl(var(--muted-foreground))",
                            borderColor: "hsl(var(--border))",
                            height: "24px",
                            fontSize: "0.75rem",
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        <Chip
                          label="Draft"
                          variant="outlined"
                          size="small"
                          style={{
                            backgroundColor: "transparent",
                            color: "hsl(var(--muted-foreground))",
                            borderColor: "hsl(var(--border))",
                            height: "24px",
                            fontSize: "0.75rem",
                          }}
                        />
                      </div>
                    )
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      {enableCorrupted && (
                        <Chip
                          label="Issue detected"
                          variant="outlined"
                          size="small"
                          icon={
                            <ErrorIcon sx={{ fontSize: "16px !important" }} />
                          }
                          sx={{
                            backgroundColor: "hsl(var(--warning) / 0.1)",
                            color: "hsl(var(--warning-foreground))",
                            borderColor: "hsl(var(--warning))",
                            height: "24px",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            "& .MuiChip-icon": {
                              color: "hsl(var(--warning-foreground))",
                            },
                          }}
                        />
                      )}
                      {!params.inspireId && createdAt !== 0 && (
                        <Chip
                          label={`Created: ${customFormat(
                            dayjs(createdAt),
                            currentTime
                          )}`}
                          variant="outlined"
                          size="small"
                          sx={{
                            backgroundColor: "transparent",
                            color: "hsl(var(--muted-foreground))",
                            borderColor: "hsl(var(--border))",
                            height: "24px",
                            fontSize: "0.75rem",
                          }}
                        />
                      )}
                      {!params.inspireId && (
                        <Chip
                          label={
                            timestamp !== 0
                              ? `Last run: ${customFormat(
                                  dayjs(timestamp),
                                  currentTime
                                )}`
                              : "Not run yet"
                          }
                          variant="outlined"
                          size="small"
                          sx={{
                            backgroundColor: "transparent",
                            color: "hsl(var(--muted-foreground))",
                            borderColor: "hsl(var(--border))",
                            height: "24px",
                            fontSize: "0.75rem",
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}

              <div
                style={{
                  marginTop: "16px",
                  marginBottom: "16px",
                  backgroundColor: "transparent",
                }}
              >
                <div
                  style={{
                    backgroundColor: "transparent",
                    border: `1px solid ${
                      edit || !editable
                        ? "hsl(var(--border))"
                        : isCorrupted
                        ? "hsl(var(--warning) / 0.5)"
                        : "hsl(var(--primary) / 0.5)"
                    }`,
                    padding: "12px",
                    borderRadius: "16px",
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    overflowY: "auto",
                    overflowX: "hidden",
                    position: "relative",
                    transition: "background-color 0.2s, border-color 0.2s",
                    cursor:
                      !edit && editable && !isCorrupted ? "pointer" : "default",
                  }}
                  onMouseEnter={(e) => {
                    if (!edit && editable && !isCorrupted) {
                      e.currentTarget.style.backgroundColor =
                        "hsl(var(--accent))";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!edit && editable && !isCorrupted) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                  onClick={
                    !edit && editable && !isCorrupted
                      ? () => enterEdit()
                      : undefined
                  }
                >
                  {isCorrupted ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "16px",
                      }}
                    >
                      <ErrorIcon
                        sx={{
                          fontSize: 48,
                          color: "hsl(var(--warning-foreground))",
                          marginBottom: 2,
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          color: "hsl(var(--warning-foreground))",
                          textAlign: "center",
                          fontWeight: 600,
                          marginBottom: 1,
                        }}
                      >
                        Data Corrupted
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "hsl(var(--warning-foreground))",
                          textAlign: "center",
                          maxWidth: "400px",
                        }}
                      >
                        This automation draft cannot be displayed correctly.
                        It&apos;s probably due to assistant generation error.
                        Please delete it.
                      </Typography>
                    </div>
                  ) : (
                    <Markdown
                      components={{
                        a:
                          edit && editable
                            ? handleMarkdownLink
                            : handleMarkdownNonEditLink,
                        input:
                          edit && editable
                            ? handleMarkdownInput
                            : handleMarkdownNonEditInput,
                        p: ({ children }) => <div>{children}</div>,
                      }}
                    >
                      {automationDescription}
                    </Markdown>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <Drawer
        anchor="bottom"
        open={showDrawer}
        onClose={handleDrawerClose}
        sx={{
          zIndex: "var(--z-index-automation-drawer)",
        }}
        PaperProps={{
          sx: {
            zIndex: "var(--z-index-automation-drawer)",
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        }}
      >
        <div style={{ padding: "16px" }}>
          {varSaving || !selectedVar ? (
            <LoadingSpinner />
          ) : (
            <DataSelector
              varName={selectedVar}
              spec={vars[selectedVar]}
              onSave={handleVarValueSave}
              onCancel={handleDrawerClose}
              onFetch={handleVarValueFetch}
              widgetized={widgetizeVars.indexOf(selectedVar) !== -1}
              widgetizable={isWidgetable(selectedVar)}
              widgetizeVar={handleWidgetize}
              setLocalWidgetizeVar={handleLocalWidgetize}
            ></DataSelector>
          )}
        </div>
      </Drawer>
      <DeleteAutomationDialog
        open={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        onDelete={handleDelete}
      ></DeleteAutomationDialog>
      <CanvasSelectDialog
        open={canvasDialogOpen}
        onClose={() => setCanvasDialogOpen(false)}
        canvasList={canvases}
        unavailableCanvasList={currentCanvases}
        onCanvasChange={handleCanvasSelect}
        title="Select Canvas"
      ></CanvasSelectDialog>
      <ShareAutomationDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        shareLink={shareLink}
        onCopy={handleCopyLink}
      />
      <SaveAsAutomationDialog
        open={saveDialogOpen}
        onClose={handleSaveDialogClose}
        onSave={handleEnrollTemp}
        onOpen={handleOpenSavedAutomation}
        automationName={automationName}
      />
    </>
  );
};

// Memoize the component to prevent unnecessary re-renders
// Only re-render when key props change (params, onSave, onDelete, editable)
const MemoizedAutomationEditCardContent = React.memo(
  AutomationEditCardContent,
  (prevProps, nextProps) => {
    // Compare the key identifying properties of params
    const keyPropsEqual =
      prevProps.params?.automationId === nextProps.params?.automationId &&
      prevProps.params?.scriptId === nextProps.params?.scriptId &&
      prevProps.params?.inspireId === nextProps.params?.inspireId &&
      prevProps.params?.category === nextProps.params?.category &&
      prevProps.params?.forceEdit === nextProps.params?.forceEdit;

    // Return true if props are equal (should NOT re-render)
    return keyPropsEqual;
  }
);

// Set display name for better debugging
MemoizedAutomationEditCardContent.displayName =
  "MemoizedAutomationEditCardContent";

export default MemoizedAutomationEditCardContent;
